"use client";

import React, { useEffect, useRef, useState } from "react";

/*
  ReservationBootstrap.tsx
  - نسخهٔ کامل با قابلیت حذف اتاق‌های اضافه‌شده
  - Bootstrap + TypeScript + RTL
  - نیاز: import کردن `bootstrap/dist/css/bootstrap.min.css` و `bootstrap-icons` در لِی‌اوت
*/

type Room = { id: number; title: string; price: number; dateLabel: string };

function normalizeDigits(value: string) {
  return value
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 1776))
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 1632));
}

function isInvalidRepeatedSequence(code: string) {
  if (!code) return false;
  for (let i = 1; i < code.length; i++) if (code[i] !== code[0]) return false;
  return true;
}

function iranianNationalIdIsValid(raw: string) {
  if (!raw) return false;
  const code = normalizeDigits(raw).toString().trim();
  if (!/^\d{10}$/.test(code)) return false;
  if (isInvalidRepeatedSequence(code)) return false;
  const check = Number(code[9]);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(code[i]) * (10 - i);
  const rem = sum % 11;
  return (rem < 2 && check === rem) || (rem >= 2 && check === 11 - rem);
}

function formatPrice(n: number) {
  return new Intl.NumberFormat("fa-IR").format(n) + " تومان";
}

export default function ReservationBootstrap() {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // رزرو کننده
  const [guestName, setGuestName] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");

  // سرپرست اتاق
  const [sameAsGuest, setSameAsGuest] = useState(false);
  const [mainContactName, setMainContactName] = useState("");
  const [mainContactNational, setMainContactNational] = useState("");
  const [hasForeignGuest, setHasForeignGuest] = useState(false);

  // اتاق‌ها
  const [rooms, setRooms] = useState<Room[]>([
    { id: 1, title: "اتاق 1: یک تخته (اقامت بدون صبحانه)", price: 600000, dateLabel: "شنبه 1404/08/11" },
  ]);

  // خطاها
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorSummaryRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (sameAsGuest) {
      setMainContactName(guestName);
      setMainContactNational(nationalId);
    }
  }, [sameAsGuest, guestName, nationalId]);

  const total = rooms.reduce((s, r) => s + r.price, 0);

  function addRoom() {
    if (rooms.length >= 5) return; // حداکثر 5 اتاق
    const newId = rooms.length ? Math.max(...rooms.map((r) => r.id)) + 1 : 1;
    setRooms([
      ...rooms,
      {
        id: newId,
        title: `اتاق ${newId}: یک تخته`,
        price: 600000,
        dateLabel: "شنبه 1404/08/11",
      },
    ]);
  }

  function removeRoom(id: number) {
    // امنیت: حداقل یک اتاق باید بماند
    if (rooms.length <= 1) return;
    setRooms(rooms.filter((r) => r.id !== id));
  }

  function validatePersonalFields(): boolean {
    const e: Record<string, string> = {};

    if (!guestName.trim()) e.guestName = "نام و نام خانوادگی رزرو کننده الزامی است.";
    else if (guestName.trim().length < 2) e.guestName = "نام وارد شده خیلی کوتاه است.";

    if (!/^[0-9\u06F0-\u06F9\u0660-\u0669]{10}$/.test(nationalId)) e.nationalId = "کد ملی باید شامل 10 رقم باشد (اعداد فارسی یا لاتین پذیرفته می‌شود).";
    else if (!iranianNationalIdIsValid(nationalId)) e.nationalId = "کد ملی وارد شده نامعتبر است.";

    if (!sameAsGuest) {
      if (!mainContactName.trim()) e.mainContactName = "نام و نام خانوادگی سرپرست الزامی است.";
      else if (mainContactName.trim().length < 2) e.mainContactName = "نام سرپرست خیلی کوتاه است.";

      if (!/^[0-9\u06F0-\u06F9\u0660-\u0669]{10}$/.test(mainContactNational)) e.mainContactNational = "کد ملی سرپرست باید شامل 10 رقم باشد.";
      else if (!iranianNationalIdIsValid(mainContactNational)) e.mainContactNational = "کد ملی سرپرست نامعتبر است.";
    }

    if (mobile.trim()) {
      const m = normalizeDigits(mobile);
      if (!/^09\d{9}$/.test(m)) e.mobile = "شماره موبایل نامعتبر است. فرمت معمول: 0912xxxxxxx";
    }

    if (email.trim()) {
      const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!re.test(email)) e.email = "آدرس ایمیل نامعتبر است.";
    }

    setErrors(e);
    if (Object.keys(e).length > 0) setTimeout(() => errorSummaryRef.current?.focus(), 100);
    return Object.keys(e).length === 0;
  }

  function handleContinueToPayment() {
    if (!validatePersonalFields()) return;
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleSimulatePayment() {
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function downloadVoucher() {
    const voucher = `واچر رزرو\nنام: ${guestName}\nمبلغ: ${formatPrice(total)}\nتعداد اتاق: ${rooms.length}`;
    const blob = new Blob([voucher], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "voucher.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  // شناسه فیلدها برای فوکوس از خلاصهٔ خطا
  const fieldIds = {
    guestName: "guestName",
    nationalId: "nationalId",
    mobile: "mobile",
    email: "email",
    mainContactName: "mainContactName",
    mainContactNational: "mainContactNational",
  } as const;

  const helpStyles = "small text-muted";

  return (
    <div className="container py-4" dir="rtl" style={{ fontFamily: "inherit" }}>
      {/* استپر */}
      <nav aria-label="مراحل رزرو" className="mb-4">
        <ol className="list-unstyled d-flex justify-content-between align-items-center gap-3 m-0">
          {[{ n: 1, label: "ورود اطلاعات" }, { n: 2, label: "پرداخت" }, { n: 3, label: "دریافت واچر" }].map((s) => (
            <li key={s.n} style={{ flex: 1 }}>
              <button
                type="button"
                className={`w-100 d-flex flex-column align-items-center border-0 bg-transparent ${step === s.n ? "" : "text-muted"}`}
                aria-current={step === s.n ? "step" : undefined}
                onClick={() => setStep(s.n as 1 | 2 | 3)}
                aria-label={`مرحله ${s.n} - ${s.label}`}
              >
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${step === s.n ? "bg-danger text-white" : "bg-white text-secondary border"}`} style={{ width: 44, height: 44 }}>
                  {s.n}
                </div>
                <div className="mt-2 small text-center">{s.label}</div>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <div className="row g-4">
        {/* خلاصه و لیست اتاق‌ها (با دکمه حذف) */}
        <aside className="col-12 col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">اقامتگاه سنتی علی بابا یزد</h5>

              <div className="mb-3 small text-secondary">
                <div className="d-flex justify-content-between"><div className="fw-semibold">تاریخ ورود</div><div>شنبه 1404/08/11 — ساعت 14:00</div></div>
                <div className="d-flex justify-content-between mt-2"><div className="fw-semibold">مدت اقامت</div><div>2 شب (دوشنبه 1404/08/13)</div></div>
              </div>

              <hr />

              <div className="mb-3">
                <div className="small text-secondary">قیمت این اقامتگاه برای 2 شب</div>
                <div className="h5 mt-1">{formatPrice(total)}</div>
                <div className="small text-warning mt-1">بدون احتساب میهمان خارجی</div>
              </div>

              <div className="mt-3">
                <div className="small text-secondary mb-2">اتاق‌ها</div>
                <div className="row g-2">
                  {rooms.map((r, idx) => (
                    <div key={r.id} className="col-6 position-relative">
                      <div className="border rounded p-2 text-center position-relative">
                        <div className="small text-secondary">{r.dateLabel}</div>
                        <div className="fw-medium">{formatPrice(r.price)}</div>

                        {/* دکمه حذف: نمایش فقط اگر بیشتر از 1 اتاق وجود داشته باشد */}
                        {rooms.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger position-absolute"
                            style={{ top: 6, left: 6 }} // در سمت چپِ کارت (چون RTL)
                            aria-label={`حذف ${r.title}`}
                            onClick={() => removeRoom(r.id)}
                          >
                            <i className="bi bi-trash" aria-hidden />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="small text-muted mt-2">اتاق انتخاب‌شده: {rooms.length} مورد</div>
              </div>

              <div className="d-flex gap-2 mt-4">
                <button
                  className="btn btn-danger flex-grow-1"
                  onClick={() => (step === 1 ? handleContinueToPayment() : step === 2 ? handleSimulatePayment() : null)}
                >
                  {step === 1 ? "ادامه و پرداخت" : step === 2 ? "پرداخت" : "—"}
                </button>
                <button className="btn btn-outline-secondary" onClick={addRoom} disabled={rooms.length >= 5}>+ افزودن اتاق جدید</button>
              </div>
            </div>
          </div>
        </aside>

        {/* فرم اصلی */}
        <main className="col-12 col-md-8">
          <div className="card shadow-sm">
            <div className="card-body">
              {/* خلاصهٔ خطا */}
              {Object.keys(errors).length > 0 && (
                <div className="alert alert-danger" role="alert" tabIndex={-1} ref={errorSummaryRef} aria-live="polite">
                  <strong>خطا در اطلاعات وارد شده:</strong>
                  <ul className="mb-0 mt-2">
                    {Object.entries(errors).map(([k, v]) => (
                      <li key={k}>
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline"
                          onClick={() => {
                            const el = document.getElementById(fieldIds[k as keyof typeof fieldIds]);
                            el?.focus();
                          }}
                        >
                          {v}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {step === 1 && (
                <form onSubmit={(e) => { e.preventDefault(); handleContinueToPayment(); }} noValidate>
                  <h4 className="mb-3">مشخصات رزروکننده</h4>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.guestName} className="form-label">نام و نام خانوادگی <span className="text-danger">*</span></label>
                      <input id={fieldIds.guestName} type="text" className={`form-control ${errors.guestName ? 'is-invalid' : guestName ? 'is-valid' : ''}`} placeholder="مثال: علی رضایی" value={guestName} onChange={(e) => setGuestName(e.target.value)} aria-required aria-invalid={!!errors.guestName} />
                      {errors.guestName ? <div className="invalid-feedback">{errors.guestName}</div> : <div className={helpStyles}>نام و نام خانوادگی باید حداقل شامل نام و نام‌خانوادگی باشد.</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.nationalId} className="form-label">کد ملی <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <input id={fieldIds.nationalId} type="text" inputMode="numeric" pattern="[0-9\u06F0-\u06F9]*" className={`form-control text-end ${errors.nationalId ? 'is-invalid' : nationalId ? (iranianNationalIdIsValid(nationalId) ? 'is-valid' : '') : ''}`} placeholder="۱۰ رقم بدون خط تیره" value={nationalId} onChange={(e) => setNationalId(e.target.value.replace(/[^0-9\u06F0-\u06F9\u0660-\u0669]/g, '').slice(0, 10))} aria-required aria-invalid={!!errors.nationalId} />
                        <span className="input-group-text bg-transparent" aria-hidden>{nationalId && (iranianNationalIdIsValid(nationalId) ? <i className="bi bi-check-circle-fill text-success" /> : <i className="bi bi-x-circle-fill text-danger" />)}</span>
                      </div>
                      {errors.nationalId ? <div className="invalid-feedback d-block">{errors.nationalId}</div> : <div className={helpStyles}>کد ملی معتبر برای ایرانیان؛ فقط ارقام وارد شوند.</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.mobile} className="form-label">شماره موبایل</label>
                      <input id={fieldIds.mobile} type="tel" className={`form-control ${errors.mobile ? 'is-invalid' : mobile ? 'is-valid' : ''}`} placeholder="مثال: 0913xxxxxxx" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^0-9\u06F0-\u06F9\u0660-\u0669]/g, ''))} aria-invalid={!!errors.mobile} />
                      {errors.mobile ? <div className="invalid-feedback">{errors.mobile}</div> : <div className={helpStyles}>اگر وارد شود برای ارسال اطلاع‌رسانی استفاده می‌شود.</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.email} className="form-label">پست الکترونیک (اختیاری)</label>
                      <input id={fieldIds.email} type="email" className={`form-control ${errors.email ? 'is-invalid' : email ? 'is-valid' : ''}`} placeholder="ایمیل خود را وارد نمایید" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!errors.email} />
                      {errors.email ? <div className="invalid-feedback">{errors.email}</div> : <div className={helpStyles}>ایمیل برای دریافت واچر و فاکتور استفاده می‌شود.</div>}
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3">مشخصات سرپرست اتاق‌ها</h5>

                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <input className="form-check-input" type="checkbox" id="sameAsGuest" checked={sameAsGuest} onChange={(e) => setSameAsGuest(e.target.checked)} aria-checked={sameAsGuest} />
                      <label className="form-check-label mb-0" htmlFor="sameAsGuest" style={{ cursor: "pointer" }}>نام رزرو کننده با سرپرست اتاق <strong>یکی می‌باشد</strong></label>
                    </div>
                    <div className="form-text text-muted">در صورت تیک: فیلدهای سرپرست با اطلاعات رزروکننده پر می‌شوند و قفل می‌گردند.</div>
                  </div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.mainContactName} className="form-label">نام و نام خانوادگی <span className="text-danger">*</span></label>
                      <input id={fieldIds.mainContactName} type="text" className={`form-control ${errors.mainContactName ? 'is-invalid' : mainContactName ? 'is-valid' : ''}`} placeholder="نام و نام خانوادگی سرپرست" value={mainContactName} onChange={(e) => setMainContactName(e.target.value)} disabled={sameAsGuest} aria-required aria-invalid={!!errors.mainContactName} />
                      {errors.mainContactName ? <div className="invalid-feedback">{errors.mainContactName}</div> : <div className={helpStyles}>نام سرپرست باید ثبت شود (حتی اگر همان رزروکننده باشد).</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor={fieldIds.mainContactNational} className="form-label">کد ملی <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <input id={fieldIds.mainContactNational} type="text" inputMode="numeric" className={`form-control text-end ${errors.mainContactNational ? 'is-invalid' : mainContactNational ? (iranianNationalIdIsValid(mainContactNational) ? 'is-valid' : '') : ''}`} placeholder="کد ملی سرپرست" value={mainContactNational} onChange={(e) => setMainContactNational(e.target.value.replace(/[^0-9\u06F0-\u06F9\u0660-\u0669]/g, '').slice(0, 10))} disabled={sameAsGuest} aria-required aria-invalid={!!errors.mainContactNational} />
                        <span className="input-group-text bg-transparent" aria-hidden>{mainContactNational && (iranianNationalIdIsValid(mainContactNational) ? <i className="bi bi-check-circle-fill text-success" /> : <i className="bi bi-x-circle-fill text-danger" />)}</span>
                      </div>
                      {errors.mainContactNational ? <div className="invalid-feedback d-block">{errors.mainContactNational}</div> : <div className={helpStyles}>کد ملی سرپرست را به صورت 10 رقم وارد کنید.</div>}
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-center gap-3">
                        <div className="small text-secondary">آیا در این اتاق میهمان خارجی اقامت خواهد داشت؟</div>
                        <div className="ms-auto btn-group" role="group" aria-label="گزینه میهمان خارجی">
                          <button type="button" className={`btn ${!hasForeignGuest ? 'btn-outline-danger' : 'btn-outline-secondary'}`} onClick={() => setHasForeignGuest(false)}>بدون میهمان خارجی</button>
                          <button type="button" className={`btn ${hasForeignGuest ? 'btn-danger text-white' : 'btn-outline-secondary'}`} onClick={() => setHasForeignGuest(true)}>با میهمان خارجی</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={addRoom} disabled={rooms.length >= 5}>+ افزودن اتاق جدید</button>
                    <button type="submit" className="btn btn-danger">ادامه و پرداخت</button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <div>
                  <h4 className="mb-3">پرداخت</h4>
                  <div className="row">
                    <div className="col-12 col-md-8">
                      <div className="card mb-3">
                        <div className="card-body">
                          <h6 className="card-title">خلاصه سفارش</h6>
                          <div className="d-flex justify-content-between"><div>اقامتگاه</div><div>اقامتگاه سنتی علی بابا یزد</div></div>
                          <div className="d-flex justify-content-between"><div>تعداد اتاق</div><div>{rooms.length}</div></div>
                          <div className="d-flex justify-content-between"><div>مجموع</div><div className="fw-bold">{formatPrice(total)}</div></div>
                        </div>
                      </div>

                      <div className="mb-3 small text-muted">روش پرداخت را انتخاب و ادامه دهید. (در نسخهٔ نمونه پرداخت شبیه‌سازی شده است.)</div>

                      <div className="d-flex gap-2 justify-content-end">
                        <button className="btn btn-secondary" onClick={() => setStep(1)}>بازگشت</button>
                        <button className="btn btn-danger" onClick={handleSimulatePayment}>پرداخت و دریافت واچر</button>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="card text-center">
                        <div className="card-body small">
                          <div className="mb-2">مبلغ قابل پرداخت</div>
                          <div className="h5 mb-2">{formatPrice(total)}</div>
                          <div className="text-muted">پرداخت امن و سریع</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h4 className="mb-3">دریافت واچر</h4>
                  <div className="alert alert-success">پرداخت با موفقیت انجام شد. واچر رزرو شما آماده است.</div>

                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="card-title">واچر رزرو</h6>
                      <div className="small text-muted">نام رزرو کننده: <strong>{guestName}</strong></div>
                      <div className="small text-muted">مبلغ: <strong>{formatPrice(total)}</strong></div>
                      <div className="small text-muted">تعداد اتاق: <strong>{rooms.length}</strong></div>
                      <div className="mt-3">
                        <button className="btn btn-primary me-2" onClick={downloadVoucher}>دانلود واچر</button>
                        <button className="btn btn-outline-secondary" onClick={() => window.print()}>چاپ</button>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-secondary" onClick={() => setStep(2)}>بازگشت به پرداخت</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
